import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import { DirectionsCar, NavigateBefore, NavigateNext } from '@mui/icons-material';
import { productsAPI, unitProductsAPI, inventoryAPI } from '../../services/api';
import { toast } from 'react-toastify';

const conditionColor = (condition) => ({
  new: 'success',
  used: 'warning',
  certified_used: 'info',
  damaged: 'error',
  refurbished: 'secondary',
  pending_detail: 'warning',
}[condition] || 'default');

const unitStatusColor = (status) => ({
  in_stock: 'success',
  reserved: 'warning',
  sold: 'default',
  returned: 'info',
  transferred: 'secondary',
  written_off: 'error',
  untracked: 'warning',
}[status] || 'default');

const UnitDetailDialog = ({ open, unitId, fallbackUnit, onClose }) => {
  const [unit, setUnit] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);

  useEffect(() => {
    if (!open || (!unitId && !fallbackUnit)) return;

    let active = true;

    const loadDetails = async () => {
      try {
        setLoading(true);
        setCurrentImageIndex(0);

        let unitData = null;
        if (fallbackUnit?.is_untracked) {
          unitData = {
            ...fallbackUnit,
            status: 'untracked',
            condition: 'pending_detail',
          };
        } else {
          const unitResponse = await unitProductsAPI.getById(unitId);
          unitData = unitResponse.data.data;
        }

        if (!active) return;
        setUnit(unitData);

        if (unitData?.product_id) {
          const productResponse = await productsAPI.getById(unitData.product_id);
          if (!active) return;
          setProduct(productResponse.data.data);
        } else {
          setProduct(null);
        }

        // Load stock movements for this unit
        if (unitId && !fallbackUnit?.is_untracked) {
          setMovementsLoading(true);
          try {
            const movementsResponse = await inventoryAPI.getMovements({ 
              limit: 200 
            });
            if (active) {
              // Filter movements that belong to this unit
              const unitMovements = (movementsResponse.data.data || []).filter(
                m => m.unit_product_id === unitId
              );
              setMovements(unitMovements);
            }
          } catch {
            if (active) setMovements([]);
          } finally {
            if (active) setMovementsLoading(false);
          }
        }
      } catch {
        if (active) {
          toast.error('Failed to load unit details');
          setUnit(null);
          setProduct(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    loadDetails();

    return () => {
      active = false;
    };
  }, [open, unitId, fallbackUnit]);

  const images = product?.images?.filter((image) => image?.url) || [];
  const isInventoryOnly = !!unit?.is_untracked;

  const handlePrevImage = () => {
    setCurrentImageIndex((index) => Math.max(index - 1, 0));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((index) => Math.min(index + 1, images.length - 1));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white',
          py: 1.5,
          px: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DirectionsCar sx={{ fontSize: 24 }} />
          <Typography variant="h5" fontWeight={900}>
            {isInventoryOnly ? 'INVENTORY ROW DETAILS' : 'UNIT DETAILS'}
          </Typography>
        </Box>
        {unit && !loading && (
          <Chip
            label={(isInventoryOnly ? 'INVENTORY ONLY' : (unit.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN'))}
            size="small"
            color={unitStatusColor(unit.status)}
            sx={{ fontWeight: 700, fontSize: '0.8rem' }}
          />
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, bgcolor: 'grey.50' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : unit ? (
          <>
            {images.length > 0 && (
              <Box
                sx={{
                  position: 'relative',
                  bgcolor: 'black',
                  height: '280px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={images[currentImageIndex].url}
                  alt={product?.name || 'Tracked unit'}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />

                {images.length > 1 && (
                  <>
                    <IconButton
                      onClick={handlePrevImage}
                      disabled={currentImageIndex === 0}
                      sx={{
                        position: 'absolute',
                        left: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        width: 40,
                        height: 40,
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' },
                        '&:disabled': { bgcolor: 'rgba(0,0,0,0.3)' },
                      }}
                    >
                      <NavigateBefore sx={{ fontSize: 24 }} />
                    </IconButton>

                    <IconButton
                      onClick={handleNextImage}
                      disabled={currentImageIndex === images.length - 1}
                      sx={{
                        position: 'absolute',
                        right: 10,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        width: 40,
                        height: 40,
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.9)' },
                        '&:disabled': { bgcolor: 'rgba(0,0,0,0.3)' },
                      }}
                    >
                      <NavigateNext sx={{ fontSize: 24 }} />
                    </IconButton>
                  </>
                )}
              </Box>
            )}

            <Box sx={{ p: 3, bgcolor: 'white' }}>
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Typography variant="h6" fontWeight={900} color="primary.main" sx={{ mb: 0.5 }}>
                  {product?.name || unit.product_name || 'Product Details'}
                </Typography>
                <Typography variant="body2" fontWeight={600} color="text.secondary">
                  SKU: {product?.sku || unit.sku || '-'}
                </Typography>
                {!!(product?.model || unit.model) && (
                  <Typography variant="caption" fontWeight={600} color="text.secondary">
                    Model: {product?.model || unit.model}
                  </Typography>
                )}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      CHASSIS
                    </Typography>
                    <Typography variant="body1" fontWeight={800}>
                      {unit.chassis_number || (isInventoryOnly ? 'Not captured yet' : '-')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      STATUS
                    </Typography>
                    <Chip
                      label={isInventoryOnly ? 'INVENTORY ONLY' : (unit.status?.replace('_', ' ').toUpperCase() || 'UNKNOWN')}
                      size="small"
                      color={unitStatusColor(unit.status)}
                      sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      CONDITION
                    </Typography>
                    <Chip
                      label={isInventoryOnly ? 'NEEDS DETAILS' : (unit.condition?.toUpperCase() || 'NEW')}
                      size="small"
                      color={conditionColor(unit.condition)}
                      sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      COLOR
                    </Typography>
                    <Typography variant="body1" fontWeight={800}>{unit.color || '-'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      YEAR
                    </Typography>
                    <Typography variant="body1" fontWeight={800}>{unit.year || '-'}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Box sx={{ textAlign: 'center', mb: 1 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      WAREHOUSE
                    </Typography>
                    <Typography variant="body1" fontWeight={800}>{unit.warehouse_name || '-'}</Typography>
                  </Box>
                </Grid>
                {isInventoryOnly && (
                  <Grid item xs={6} sm={4}>
                    <Box sx={{ textAlign: 'center', mb: 1 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        QUANTITY
                      </Typography>
                      <Typography variant="body1" fontWeight={800}>{unit.quantity || 0}</Typography>
                    </Box>
                  </Grid>
                )}
                {!isInventoryOnly && (
                  <>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ textAlign: 'center', mb: 1 }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                          RECEIVED
                        </Typography>
                        <Typography variant="body1" fontWeight={800}>
                          {unit.created_at ? new Date(unit.created_at).toLocaleDateString() : '-'}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Box sx={{ textAlign: 'center', mb: 1 }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                          TRACKED BY
                        </Typography>
                        <Typography variant="body1" fontWeight={800}>{unit.created_by_name || '-'}</Typography>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>

              {product?.description && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'primary.main' }}>
                    DESCRIPTION
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ lineHeight: 1.5, fontSize: '0.9rem' }}>
                    {product.description}
                  </Typography>
                </Box>
              )}

              {isInventoryOnly && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'warning.dark' }}>
                    UNIT DETAILS NOT CAPTURED
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.9rem' }}>
                    This stock exists in inventory, but the chassis-level unit record has not been added yet.
                    Add or receive stock with unit details to turn this into a full tracked unit.
                  </Typography>
                </Box>
              )}

              {!!unit?.notes && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'info.dark' }}>
                    NOTES
                  </Typography>
                  <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.9rem' }}>
                    {unit.notes}
                  </Typography>
                </Box>
              )}

              {!isInventoryOnly && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'primary.main' }}>
                      STOCK MOVEMENT HISTORY
                    </Typography>
                    {movementsLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : movements.length > 0 ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Source Warehouse</TableCell>
                            <TableCell>Dest Warehouse</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell>Reference</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {movements.map((movement) => (
                            <TableRow key={movement.id} hover>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {new Date(movement.created_at).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={movement.movement_type.replace(/_/g, ' ').toUpperCase()}
                                  size="small"
                                  color={movement.movement_type === 'in' || movement.movement_type === 'return' ? 'success' : movement.movement_type === 'out' || movement.movement_type === 'transfer' ? 'warning' : 'default'}
                                  sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                                />
                              </TableCell>
                              <TableCell>{movement.from_warehouse_name || movement.warehouse_name || '-'}</TableCell>
                              <TableCell>{movement.to_warehouse_name || '-'}</TableCell>
                              <TableCell align="right">
                                <Typography fontWeight={700}>
                                  {movement.movement_type === 'out' || movement.movement_type === 'transfer' ? '-' : '+'}{movement.quantity}
                                </Typography>
                              </TableCell>
                              <TableCell>{movement.reference_type || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No stock movements recorded for this unit yet
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: 'white' }}>
        <Button onClick={onClose} variant="contained" sx={{ fontWeight: 700 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnitDetailDialog;
